import { useCallback, useEffect, useRef, useState } from "react";
import { Storage, asyncStorageStorage } from "@lookiero/sty-psp-storage";
import { FeedbackProjection } from "../../../../../../projection/feedback/feedback";

const RETURN_FEEDBACK_DRAFT_KEY_PREFIX = "checkout:return-feedback-draft:";
const RETURN_FEEDBACK_DRAFT_DEBOUNCE_MS = 300;

const returnQuestionFeedbackDraftKey = (checkoutItemId: string): string =>
  `${RETURN_FEEDBACK_DRAFT_KEY_PREFIX}${checkoutItemId}`;

interface UseReturnQuestionFeedbackDraftFunctionArgs {
  readonly checkoutItemId: string | undefined;
}

interface PersistDraftFunction {
  (feedback: FeedbackProjection): void;
}

interface ClearDraftFunction {
  (): void;
}

interface UseReturnQuestionFeedbackDraftFunction {
  (args: UseReturnQuestionFeedbackDraftFunctionArgs): {
    readonly draft: FeedbackProjection;
    readonly loaded: boolean;
    readonly persist: PersistDraftFunction;
    readonly clear: ClearDraftFunction;
  };
}

const useReturnQuestionFeedbackDraft: UseReturnQuestionFeedbackDraftFunction = ({ checkoutItemId }) => {
  const storageRef = useRef<Storage<FeedbackProjection> | undefined>(undefined);
  if (!storageRef.current) {
    storageRef.current = asyncStorageStorage<FeedbackProjection>();
  }
  const storage = storageRef.current;

  const [draft, setDraft] = useState<FeedbackProjection>({});
  const [loaded, setLoaded] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingFeedbackRef = useRef<FeedbackProjection | undefined>(undefined);
  const submittedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    submittedRef.current = false;
    setLoaded(false);

    if (!checkoutItemId) {
      setDraft({});
      setLoaded(true);
      return;
    }

    (async (): Promise<void> => {
      try {
        const storedDraft = await storage.read(returnQuestionFeedbackDraftKey(checkoutItemId));

        if (!cancelled) {
          setDraft(storedDraft && typeof storedDraft === "object" ? storedDraft : {});
        }
      } catch {
        if (!cancelled) {
          setDraft({});
        }
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkoutItemId, storage]);

  const persist = useCallback<PersistDraftFunction>(
    (feedback) => {
      if (!checkoutItemId || submittedRef.current) {
        return;
      }

      pendingFeedbackRef.current = feedback;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        debounceRef.current = undefined;

        const pendingFeedback = pendingFeedbackRef.current;
        pendingFeedbackRef.current = undefined;

        if (pendingFeedback && !submittedRef.current) {
          storage.write(returnQuestionFeedbackDraftKey(checkoutItemId), pendingFeedback);
        }
      }, RETURN_FEEDBACK_DRAFT_DEBOUNCE_MS);
    },
    [checkoutItemId, storage],
  );

  const clear = useCallback<ClearDraftFunction>(() => {
    submittedRef.current = true;
    pendingFeedbackRef.current = undefined;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }

    if (checkoutItemId) {
      storage.remove(returnQuestionFeedbackDraftKey(checkoutItemId));
    }
  }, [checkoutItemId, storage]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }

      const pendingFeedback = pendingFeedbackRef.current;
      pendingFeedbackRef.current = undefined;

      if (pendingFeedback && !submittedRef.current && checkoutItemId) {
        storage.write(returnQuestionFeedbackDraftKey(checkoutItemId), pendingFeedback);
      }
    },
    [checkoutItemId, storage],
  );

  return { draft, loaded, persist, clear };
};

export type { UseReturnQuestionFeedbackDraftFunction };
export { useReturnQuestionFeedbackDraft, returnQuestionFeedbackDraftKey };
